import React, { useCallback, useEffect, useState } from 'react';

import { Confidence } from '../confidence/model';
import { Effort } from '../effort/model';
import { Priority } from '../priority/model';
import { State } from '../state/model';

const requiredFieldMsg = 'Required field';

export interface FormData {
  assigneeId?: string;
  confidence: Confidence;
  createNew: boolean;
  dueDateTimestamp?: number;
  effort: Effort;
  fromDateTimestamp?: number;
  title: string;
  priority: Priority;
  relationshipType?: string;
  stateId: number;
  workstreamId?: string;
  assigneePic?: string;
  assigneeFullName?: string;
  workstreamName?: string;
}

export type FormErrors = { [key in keyof FormData]?: string };

export function useCopyAIForm(
  initialData: Partial<FormData>,
  isProcessing: boolean
) {
  const initialFormData = initialData;
  const [formData, setFormData] = useState<Partial<FormData>>(initialFormData);
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);

  const validate = useCallback((validatedFormData: FormData) => {
    const errors: FormErrors = {};

    if (!validatedFormData.title) {
      errors.title = requiredFieldMsg;
    }
    if (typeof validatedFormData.workstreamId === 'undefined') {
      errors.workstreamId = requiredFieldMsg;
    }
    if (typeof validatedFormData.assigneeId === 'undefined') {
      errors.assigneeId = requiredFieldMsg;
    }
    if (!(validatedFormData.confidence in Confidence)) {
      errors.confidence = requiredFieldMsg;
    }
    if (!(validatedFormData.effort in Effort)) {
      errors.effort = requiredFieldMsg;
    }
    if (!(validatedFormData.priority in Priority)) {
      errors.priority = requiredFieldMsg;
    }
    if (!(validatedFormData.stateId in State)) {
      errors.stateId = requiredFieldMsg;
    }
    if (
      validatedFormData.fromDateTimestamp &&
      validatedFormData.dueDateTimestamp &&
      validatedFormData.fromDateTimestamp > validatedFormData.dueDateTimestamp
    ) {
      errors.fromDateTimestamp = 'Must be earlier than End Date';
      errors.dueDateTimestamp = 'Must be later than Start Date';
    }
    const result = Object.keys(errors).length ? errors : null;
    setFormErrors(result);
    return result;
  }, []);

  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData, setFormData]);

  useEffect(() => {
    validate(formData as FormData);
  }, [validate, formData]);

  const valueSetter = useCallback(
    <K extends keyof FormData>(
      name: K,
      value: FormData[K] | React.ChangeEvent<HTMLInputElement>
    ) => {
      const strVal =
        value && typeof value === 'object' ? value.target?.value : value;

      setFormData((val) => ({
        ...val,
        [name]: strVal,
      }));
    },
    []
  );

  const getFieldProps = useCallback(
    <K extends keyof FormData>(
      name: K,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getVal?: (obj: any) => FormData[K]
    ) => ({
      isDisabled: isProcessing,
      name,
      id: name,
      onChange: (e: React.ChangeEvent<HTMLInputElement>, obj?: unknown) => {
        valueSetter(
          name,
          getVal ? getVal(typeof obj !== 'undefined' ? obj : e) : e
        );
      },
      ...(formErrors?.[name]
        ? {
            helperText: formErrors?.[name],
            isError: Boolean(formErrors?.[name]),
          }
        : {}),
    }),
    [formErrors, valueSetter, isProcessing]
  );

  return {
    formData,
    formErrors,
    valueSetter,
    getFieldProps,
  };
}
